import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import LogoDark from "../../images/logo/logo-dark.svg";
import Logo from "../../images/logo/logo.svg";
import SvgIcon from "../../components/SvgIcon/SvgIcon";
import SelectField from "../../components/Forms/Input/SelectField";
import ButtonCustom from "../UiElements/ButtonCustom";
import { useAuth } from "../../auth/hooks/useAuth";
import { BranchOption } from "../../types/auth/auth.types";

/**
 * Último paso del login para un usuario con más de 1 sucursal asignada
 * (hoy solo aplica a quien tenga 2 sucursales configuradas manualmente).
 */
const SelectBranch: React.FC = () => {
  const location = useLocation();
  const { handleSelectBranch, error } = useAuth();

  const email = location.state?.email || "";
  const branches: BranchOption[] = location.state?.branches || [];

  const [selectedBranchId, setSelectedBranchId] = useState<string | number>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedBranchId) return;

    setIsSubmitting(true);
    try {
      await handleSelectBranch({ email, branchId: Number(selectedBranchId) });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-white p-4 dark:bg-boxdark-2 dark:text-bodydark">
      {/* 🎨 Mismo fondo de burbujas que el login */}
      <div className="pointer-events-none absolute inset-0 z-0 dark:opacity-40">
        <SvgIcon name="background-bubbles" width="100%" height="100%" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-md rounded-3xl border border-stroke bg-white p-8 shadow-[0_20px_60px_rgba(60,80,224,0.15)] dark:border-strokedark dark:bg-boxdark sm:p-10">
        <Link to="/" className="mb-6 flex justify-center">
          <img className="block h-auto w-40 dark:hidden" src={LogoDark} alt="Codenova" />
          <img className="hidden h-auto w-40 dark:block" src={Logo} alt="Codenova" />
        </Link>

        <div className="mb-8 text-center">
          <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <SvgIcon name="store" height={26} width={26} />
          </span>
          <h2 className="text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
            Elige tu sucursal
          </h2>
          <p className="mt-2 text-sm text-body dark:text-bodydark">
            Tu usuario tiene más de una sucursal asignada. Elige con cuál vas
            a trabajar en esta sesión.
          </p>
        </div>

        {error && (
          <div className="mb-6 flex items-center gap-2 rounded-lg bg-danger/10 p-3 text-sm text-danger">
            <AlertCircle size={18} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <SelectField
            label="Sucursal"
            name="branchId"
            iconKey="branch"
            placeholder="Seleccione una sucursal"
            value={selectedBranchId}
            onChange={(e: any) => setSelectedBranchId(e.target.value)}
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
            required
          />

          <div className="mt-2">
            <ButtonCustom
              text={isSubmitting ? "Entrando..." : "Entrar"}
              iconKey="arrow-right"
              type="submit"
              disabled={!selectedBranchId || isSubmitting}
              fullWidth
              size="lg"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default SelectBranch;
